import { Button } from "@volca/ui/components/ui/button";
import {
  BREAKER_PANEL,
  GAS_METER,
  WATER_HEATER,
} from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { GoalChecks } from "@/profitability/goal-checks";
import { TemplateRunPreview } from "@/profitability/template-chat/workspace/template-run-preview";
import type { ProfitabilityReport } from "@/profitability/types";

/**
 * An agent before it is published, as the one thing to look at while Volca
 * builds it in Edit agent beside it: nothing yet, the build running or
 * stuck, then its sample week drawn as a period will be, after how its
 * figures check against the owner's goal file. Publishing (in the
 * header) is the step after; questions the build stopped on are answered in
 * the notice above.
 */
export const AgentBuilder = ({
  businessId,
  report,
  editing,
  onEdit,
}: {
  businessId: string;
  report: ProfitabilityReport;
  /** Edit agent is open beside the page. */
  editing: boolean;
  onEdit: () => void;
}) => {
  const edit = !editing && (
    <Button onClick={onEdit}>
      {report.build_status ? "Open Edit agent" : "Describe your report"}
    </Button>
  );
  if (report.build_status === "building")
    return (
      <Stack role="status" align="stretch" className="w-full flex-1">
        <MachineEmptyState
          art={WATER_HEATER}
          layout="workspace"
          active
          title="Volca is building your agent"
          description="It reads your files and works out the figures and checks. This takes a few minutes, and you can leave this page."
        />
      </Stack>
    );
  if (report.build_status === "failed")
    return (
      <MachineEmptyState
        art={BREAKER_PANEL}
        layout="workspace"
        title="The build didn't finish"
        description="Tell Volca what to change in Edit agent, or use Rebuild in the actions menu to try again with the same files."
      >
        {edit}
      </MachineEmptyState>
    );
  if (report.build_status === "needs_input") return null;
  const checks = report.checks?.draft;
  if (report.draft_sample_run_id)
    return (
      <Stack gap={4} align="stretch" className="w-full min-w-0">
        <Text tone="muted" className="text-pretty">
          The sample week Volca built from your files, laid out as every week
          will be once you publish. Ask Volca in Edit agent for any change.
        </Text>
        {checks && (
          <Stack gap={2} align="stretch">
            <Text weight="medium">Against your figures</Text>
            <GoalChecks checks={checks} />
          </Stack>
        )}
        <TemplateRunPreview
          key={report.draft_sample_run_id}
          businessId={businessId}
          runId={report.draft_sample_run_id}
        />
      </Stack>
    );
  return (
    <MachineEmptyState
      art={GAS_METER}
      layout="workspace"
      title="Describe your report to Volca"
      description="Say what it should show and what to flag, and attach a week of the files you'll upload. Volca builds a sample week for you to check before you publish."
    >
      {edit}
    </MachineEmptyState>
  );
};
